/**
 * Mega menu v2 (desktop)
 * - Hover opens the panel; clicking the top link goes to its category page.
 * - Keyboard: Enter/Space on the top link toggles the panel, Escape closes it.
 * - Hovering (or focusing) a submenu link shows its product grid, after a short
 *   delay so a diagonal move toward the products doesn't switch grids on the way.
 */
(() => {
  if (customElements.get('mega-v2-details')) return;

  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 200;
  const SWITCH_DELAY = 120;

  let isScrolling = false;
  let scrollTimer;
  window.addEventListener('scroll', () => {
    isScrolling = true;
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => { isScrolling = false; }, 100);
  }, { passive: true });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details.mega-v2-details[open]').forEach((details) => {
      const hadFocus = details.contains(document.activeElement);
      details.closePanel();
      if (hadFocus) details.summary.focus();
    });
  });

  class MegaV2Details extends HTMLDetailsElement {
    connectedCallback() {
      if (this.summary) return;
      this.summary = this.querySelector('summary');
      this.panel = this.querySelector('[data-mega-v2-panel]');
      this.header = this.closest('.header');

      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
      this.panel?.addEventListener('mouseover', this.onPanelMouseOver.bind(this));
      this.panel?.addEventListener('focusin', this.onPanelFocusIn.bind(this));

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', () => this.openPanel());
        this.addEventListener('shopify:block:deselect', () => this.closePanel());
      }
    }

    onMouseEnter() {
      clearTimeout(this.closeTimer);
      if (isScrolling || this.open) return;
      this.loadImages(this.activeGrid);
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => this.openPanel(), OPEN_DELAY);
    }

    onMouseLeave() {
      clearTimeout(this.openTimer);
      clearTimeout(this.switchTimer);
      this.closeTimer = setTimeout(() => this.closePanel(), CLOSE_DELAY);
    }

    onSummaryClick(event) {
      event.preventDefault();
      // detail is 0 for keyboard-generated clicks (Enter/Space)
      if (event.detail === 0) {
        this.open ? this.closePanel() : this.openPanel();
        return;
      }
      if (this.summary.dataset.link) window.location.href = this.summary.dataset.link;
    }

    onPanelMouseOver(event) {
      const trigger = event.target.closest('[data-mega-v2-trigger]');
      if (!trigger) {
        clearTimeout(this.switchTimer);
        this.pendingIndex = null;
        return;
      }
      const index = trigger.dataset.megaV2Trigger;
      if (trigger.classList.contains('is-active') || index === this.pendingIndex) return;

      this.loadImages(this.gridAt(index));
      clearTimeout(this.switchTimer);
      this.pendingIndex = index;
      this.switchTimer = setTimeout(() => this.activate(index), SWITCH_DELAY);
    }

    onPanelFocusIn(event) {
      const trigger = event.target.closest('[data-mega-v2-trigger]');
      if (trigger) this.activate(trigger.dataset.megaV2Trigger);
    }

    openPanel() {
      clearTimeout(this.closeTimer);
      if (this.open) return;
      document.querySelectorAll('details.mega-v2-details[open]').forEach((details) => {
        if (details !== this) details.closePanel();
      });
      if (this.header) {
        this.style.setProperty('--mega-v2-top', `${Math.round(this.header.getBoundingClientRect().bottom)}px`);
      }
      this.activate('0');
      this.open = true;
    }

    closePanel() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.switchTimer);
      this.pendingIndex = null;
      this.open = false;
    }

    activate(index) {
      clearTimeout(this.switchTimer);
      this.pendingIndex = null;
      this.panel?.querySelectorAll('[data-mega-v2-trigger]').forEach((trigger) => {
        trigger.classList.toggle('is-active', trigger.dataset.megaV2Trigger === index);
      });
      this.panel?.querySelectorAll('[data-mega-v2-grid]').forEach((grid) => {
        grid.classList.toggle('is-active', grid.dataset.megaV2Grid === index);
      });
      this.loadImages(this.gridAt(index));
    }

    gridAt(index) {
      return this.panel?.querySelector(`[data-mega-v2-grid="${index}"]`);
    }

    get activeGrid() {
      return this.panel?.querySelector('[data-mega-v2-grid].is-active');
    }

    // Grids are lazy images inside a closed <details>; start the downloads just before they show.
    loadImages(grid) {
      grid?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }
  }

  customElements.define('mega-v2-details', MegaV2Details, { extends: 'details' });
})();
